import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { isPlatformBrowser } from '@angular/common';
import { News } from '../models/news.interface';

@Injectable({
  providedIn: 'root'
})
export class SeoService {
  private readonly baseUrl = 'https://borde.news';
  private readonly defaultTitle = 'Borde - Noticias de Actualidad';
  private readonly defaultDescription = 'Mantente informado con las últimas noticias de política, economía, deportes y más en Borde.';
  private readonly defaultImage = `${this.baseUrl}/assets/images/default-og.png`;

  constructor(
    private meta: Meta,
    private title: Title,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  private isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  setDefaultMetaTags(): void {
    if (!this.isBrowser()) return;

    this.title.setTitle(this.defaultTitle);

    this.updateOrCreateTag('description', this.defaultDescription);
    this.updateOrCreateTag('keywords', 'noticias, borde, actualidad, política, economía, deportes');
    this.updateOrCreateTag('author', 'Borde');

    // Open Graph
    this.updateOrCreateTag('og:site_name', 'Borde', 'property');
    this.updateOrCreateTag('og:title', this.defaultTitle, 'property');
    this.updateOrCreateTag('og:description', this.defaultDescription, 'property');
    this.updateOrCreateTag('og:type', 'website', 'property');
    this.updateOrCreateTag('og:url', this.baseUrl, 'property');
    this.updateOrCreateTag('og:image', this.defaultImage, 'property');

    // Twitter
    this.updateOrCreateTag('twitter:card', 'summary_large_image');
    this.updateOrCreateTag('twitter:title', this.defaultTitle);
    this.updateOrCreateTag('twitter:description', this.defaultDescription);
    this.updateOrCreateTag('twitter:image', this.defaultImage);
  }

  updateNewsMetaTags(article: News): void {
    if (!this.isBrowser() || !article) return;

    const articleUrl = `${this.baseUrl}/#/articulo/${article.slug}`;
    const description = article.metaDescription || article.excerpt || this.defaultDescription;
    const image = article.imageUrl || article.imagePreview || this.defaultImage;

    this.title.setTitle(`${article.title} | Borde`);

    this.updateOrCreateTag('description', description);

    // Open Graph
    this.updateOrCreateTag('og:title', article.title, 'property');
    this.updateOrCreateTag('og:description', description, 'property');
    this.updateOrCreateTag('og:type', 'article', 'property');
    this.updateOrCreateTag('og:url', articleUrl, 'property');
    this.updateOrCreateTag('og:image', image, 'property');

    // Extra article meta
    if (article.author) this.updateOrCreateTag('article:author', article.author, 'property');
    if (article.category) this.updateOrCreateTag('article:section', article.category, 'property');
    if (article.publishDate) this.updateOrCreateTag('article:published_time', new Date(article.publishDate as any).toISOString(), 'property');
    if (article.tags && article.tags.length) this.updateOrCreateTag('article:tag', article.tags.join(','), 'property');

    // Twitter
    this.updateOrCreateTag('twitter:title', article.title);
    this.updateOrCreateTag('twitter:description', description);
    this.updateOrCreateTag('twitter:image', image);
  }

  clearArticleMetaTags(): void {
    if (!this.isBrowser()) return;

    const propertiesToClear = [
      'og:title',
      'og:description',
      'og:type',
      'og:url',
      'og:image',
      'article:author',
      'article:section',
      'article:published_time',
      'article:tag',
      'twitter:title',
      'twitter:description',
      'twitter:image'
    ];

    propertiesToClear.forEach(prop => {
      this.meta.removeTag(`property='${prop}'`);
      this.meta.removeTag(`name='${prop}'`);
    });

    // Restaurar metas por defecto
    this.setDefaultMetaTags();
  }

  private updateOrCreateTag(name: string, content: string, attr: 'name' | 'property' = 'name'): void {
    const selector = `${attr}='${name}'`;
    if (this.meta.getTag(selector)) {
      this.meta.updateTag({ [attr]: name, content });
    } else {
      this.meta.addTag({ [attr]: name, content });
    }
  }
}